import React, { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { SettingsIcon, LogoutIcon } from '../icons';

interface Props {
  firstName: string;
  lastName: string;
}

export default function UserAvatar({ firstName, lastName }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleAccountSettings = () => {
    handleClose();
    navigate('/parent/home');
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-label={`${firstName} ${lastName}`}
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen(e as unknown as React.MouseEvent<HTMLElement>)}
        sx={(theme) => ({
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: theme.app.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontFamily: theme.typography.fontFamily,
          fontSize: '16.8px',
          fontWeight: 600,
          lineHeight: '24px',
          color: theme.app.foreground,
          userSelect: 'none',
          cursor: 'pointer',
        })}
      >
        {initials}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: '4px',
              backgroundColor: theme.app.card,
              border: `1px solid ${theme.app.borderStrong}`,
              borderRadius: '12px',
              p: '8px',
              width: 258,
              boxShadow:
                '0 2px 4px -2px rgba(0,0,0,0.10), 0 4px 6px -1px rgba(0,0,0,0.10)',
            }),
          },
          list: { sx: { p: 0 } },
        }}
      >
        <MenuItem
          onClick={handleAccountSettings}
          sx={(theme) => ({
            borderRadius: '6px',
            px: 2,
            py: 1,
            minHeight: 40,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            fontFamily: theme.typography.fontFamily,
            color: theme.app.foreground,
            '&:hover': { backgroundColor: theme.app.secondary },
          })}
        >
          <SettingsIcon size={20} />
          {t('userMenu.accountSettings')}
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={(theme) => ({
            borderRadius: '6px',
            px: 2,
            py: 1,
            minHeight: 40,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            fontFamily: theme.typography.fontFamily,
            color: theme.app.foreground,
            '&:hover': { backgroundColor: theme.app.secondary },
          })}
        >
          <LogoutIcon size={20} />
          {t('userMenu.logout')}
        </MenuItem>
      </Menu>
    </>
  );
}
